export function initMoviePlayback(area, { reducedMotion, revealActivity, document = globalThis.document, Observer = globalThis.IntersectionObserver, clock = globalThis }) {
  const movie = area.querySelector('video');
  if (!movie || (!movie.getAttribute('src') && !movie.querySelector('source[src]'))) return () => {};
  if (area.matches('.mv')) return initNativeMoviePlayback(area, movie, document, clock);
  const fallback = area.querySelector('[data-video-fallback]');
  const mp4 = movie.querySelector('source[type="video/mp4"]');
  const cleanups = [];
  let inView = false;
  let disposed = false;
  let failed = false;
  let blocked = false;
  let switched = false;
  let attempt = 0;
  let pending = false;
  movie.muted = movie.defaultMuted = true;
  movie.playsInline = true;
  if (area.matches('.mv')) movie.playbackRate = .75;
  const listen = (target, type, callback) => {
    target.addEventListener(type, callback);
    cleanups.push(() => target.removeEventListener(type, callback));
  };
  const eligible = () => inView && !document.hidden && !revealActivity?.active && !reducedMotion.matches;
  const pause = () => {
    attempt++;
    pending = false;
    movie.autoplay = false;
    movie.pause();
    area.classList.remove('is-playing');
  };
  const switchToMp4 = () => {
    if (!mp4 || switched || movie.currentSrc === mp4.src) return false;
    switched = true;
    pause();
    movie.src = mp4.src;
    movie.hidden = false;
    movie.load();
    return true;
  };
  const showFailure = () => {
    failed = true;
    pause();
    movie.hidden = true;
    if (fallback) fallback.hidden = false;
  };
  const play = () => {
    if (disposed || pending || !movie.paused) return;
    const request = ++attempt;
    pending = true;
    movie.play().then(() => {
      if (disposed || request !== attempt) return;
      pending = false;
      if (!eligible()) pause();
    }).catch((error) => {
      if (disposed || request !== attempt) return;
      pending = false;
      if (!eligible()) return;
      if (error.name === 'NotAllowedError' || error.name === 'AbortError') {
        blocked = true;
        pause();
      } else if (error.name === 'NotSupportedError' && switchToMp4()) {
        sync();
      } else showFailure();
    });
  };
  const sync = () => {
    if (disposed) return;
    movie.autoplay = eligible() && !blocked && !failed;
    if (movie.autoplay) play();
    else pause();
  };
  listen(movie, 'playing', () => {
    if (disposed || !eligible()) { pause(); return; }
    pending = false;
    blocked = failed = false;
    area.classList.add('is-playing');
  });
  listen(movie, 'error', () => {
    if (disposed) return;
    if (switchToMp4()) sync();
    else showFailure();
  });
  listen(movie, 'canplay', sync);
  const observer = new Observer(([entry]) => { inView = entry.isIntersecting; sync(); });
  observer.observe(area);
  listen(document, 'visibilitychange', sync);
  listen(reducedMotion, 'change', sync);
  if (revealActivity) cleanups.push(revealActivity.subscribe(sync));
  else sync();
  return () => {
    disposed = true;
    pause();
    observer.disconnect();
    cleanups.forEach((cleanup) => cleanup());
  };
}

// Start after this MV's initial reveal; later decorative effects never pause it.
function initNativeMoviePlayback(area, movie, document, clock) {
  const cleanups = [];
  const mp4 = movie.querySelector('source[type="video/mp4"]');
  const fallback = area.querySelector('[data-video-fallback]');
  let disposed = false;
  let pending = false;
  let switched = false;
  let advanced = false;
  let blocked = false;
  let fallbackShown = false;
  let attempt = 0;
  let startupTimer;
  let fallbackTimer;
  let revealFrame;
  let waitingForReveal = area.classList.contains('area-reveal');
  const initialTime = movie.currentTime;
  const trace = (event, detail = {}) => {
    movie.dispatchEvent(new CustomEvent('ffd:mvdebug', {
      detail: { event, attempt, waiting: waitingForReveal, pending, ...detail },
    }));
  };
  const clearStartupTimer = () => {
    clock.clearTimeout(startupTimer);
    startupTimer = undefined;
  };
  const clearFallbackTimer = () => {
    clock.clearTimeout(fallbackTimer);
    fallbackTimer = undefined;
  };
  const showFallback = () => {
    clearFallbackTimer();
    if (disposed || !fallback) return;
    fallbackShown = true;
    movie.hidden = true;
    fallback.hidden = false;
    area.classList.remove('is-playing');
    // A denied load never fires canplay or error. Release the reveal with the image.
    movie.dispatchEvent(new Event('ffd:mvfallback'));
  };
  const watchFallback = () => {
    if (disposed || document.hidden || !fallback || fallbackShown || advanced
        || movie.readyState >= 3 || fallbackTimer !== undefined) return;
    fallbackTimer = clock.setTimeout(() => {
      if (document.hidden || movie.readyState >= 3) clearFallbackTimer();
      else showFallback();
    }, 2000);
  };
  const switchToMp4 = () => {
    if (disposed || blocked) return;
    showFallback();
    if (switched || !mp4 || movie.currentSrc === mp4.src) return;
    switched = true;
    clearStartupTimer();
    // load() aborts the old play promise; it must not overwrite the new attempt.
    attempt++;
    pending = false;
    delete movie.dataset.playbackError;
    movie.src = mp4.src;
    trace('fallback');
    movie.load();
    movie.playbackRate = .75;
    play();
  };
  const checkProgress = () => {
    if (Math.abs(movie.currentTime - initialTime) <= .05) return;
    advanced = true;
    clearStartupTimer();
    clearFallbackTimer();
  };
  const watchStartup = () => {
    checkProgress();
    if (disposed || document.hidden || blocked || advanced || switched || startupTimer !== undefined) return;
    // A selected source can hang without an error, or with a pending play promise.
    // The next <source> is not automatically tried after this kind of failure.
    startupTimer = clock.setTimeout(() => {
      startupTimer = undefined;
      checkProgress();
      if (!document.hidden && !advanced) switchToMp4();
    }, 8000);
  };
  movie.muted = movie.defaultMuted = true;
  movie.playsInline = true;
  movie.autoplay = true;
  movie.playbackRate = .75;
  const play = () => {
    if (disposed || blocked || waitingForReveal || document.hidden || pending || !movie.paused) {
      trace('skip', { reason: disposed ? 'disposed' : blocked ? 'blocked' : waitingForReveal ? 'reveal' : document.hidden ? 'hidden' : pending ? 'pending' : 'already-playing' });
      return;
    }
    const request = ++attempt;
    pending = true;
    movie.hidden = false;
    trace('request');
    Promise.resolve(movie.play()).then(() => {
      trace('resolved', { reason: request === attempt ? 'current' : 'superseded' });
    }).catch((error) => {
      trace('rejected', { name: error.name, message: error.message, reason: request === attempt ? 'current' : 'superseded' });
      if (disposed || request !== attempt) return;
      movie.dataset.playbackError = error.name;
      if (error.name === 'NotAllowedError') {
        blocked = true;
        movie.autoplay = false;
        clearStartupTimer();
        showFallback();
      } else if (error.name === 'NotSupportedError') switchToMp4();
      else showFallback();
    }).finally(() => { if (request === attempt) pending = false; });
  };
  const listen = (target, type, callback) => {
    target.addEventListener(type, callback);
    cleanups.push(() => target.removeEventListener(type, callback));
  };
  const onReady = () => {
    if (movie.readyState >= 3) clearFallbackTimer();
    play();
  };
  listen(movie, 'canplay', onReady);
  listen(movie, 'loadeddata', onReady);
  listen(movie, 'ffd:revealend', () => {
    if (!waitingForReveal || revealFrame !== undefined) return;
    // The reveal owner has removed opacity/mask classes. Start on the next frame.
    revealFrame = clock.requestAnimationFrame(() => {
      revealFrame = undefined;
      if (disposed) return;
      waitingForReveal = false;
      trace('reveal-ready');
      play();
      watchStartup();
    });
  });
  listen(movie, 'error', switchToMp4);
  listen(movie, 'timeupdate', checkProgress);
  listen(movie, 'playing', () => {
    if (disposed) return;
    clearFallbackTimer();
    movie.hidden = false;
    delete movie.dataset.playbackError;
    area.classList.add('is-playing');
  });
  listen(document, 'visibilitychange', () => {
    if (document.hidden) { clearStartupTimer(); clearFallbackTimer(); }
    else { play(); watchStartup(); watchFallback(); }
  });
  if (document.defaultView) listen(document.defaultView, 'pageshow', play);
  trace('init');
  // Loading must precede the reveal's canplay gate. Do not restart an active load.
  if (movie.readyState === 0 && movie.networkState === 0) {
    trace('prepare');
    movie.load();
    movie.playbackRate = .75;
  }
  play();
  watchStartup();
  watchFallback();
  return () => {
    disposed = true;
    if (revealFrame !== undefined) clock.cancelAnimationFrame(revealFrame);
    clearStartupTimer();
    clearFallbackTimer();
    cleanups.forEach((cleanup) => cleanup());
    area.classList.remove('is-playing');
  };
}
